import store from '@/store';
import { reportErrorToTelegram } from '@/services/errorReporter';

// Detecta se a aplicação está rodando em localhost para resolver a URL correspondente
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export const API_BASE_URL = isLocalhost 
  ? 'http://localhost/anjinhoazul/backend/api/v1' 
  : 'https://anjinhoazul.com.br/backend/api/v1';

/**
 * Helper unificado para requisições fetch com tratamento global de erros PHP/Servidor
 * Inclui token de autenticação automaticamente quando disponível.
 */
export async function apiRequest(path, options = {}) {
  // Permite sobrescrever a URL dinamicamente via localStorage caso necessário
  const baseUrl = localStorage.getItem('API_URL') || API_BASE_URL;
  const url = `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

  // Monta headers com token de autenticação se disponível
  const authToken = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
    ...(options.headers || {})
  };

  const config = {
    ...options,
    headers
  };

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);
    let data = null;
    
    const text = await response.text();
    try {
      data = JSON.parse(text);
    } catch (e) {
      // Se não for JSON válido (erro bruto do PHP sem captura de exceção)
      if (!response.ok || text.length > 0) {
        throw new Error(text.substring(0, 500) || `Código de resposta HTTP ${response.status}`);
      }
    }

    // Sessão expirada ou token inválido: força logout (ignora para rotas de login)
    const isLoginPath = path.includes('auth/login') || path.includes('auth/school-login') || path.includes('nakota/auth/login');
    if (response.status === 401 && data && data.status === 'error' && !isLoginPath) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      
      store.dispatch('showToast', {
        title: 'Sessão Expirada',
        message: 'Sua sessão expirou. Faça login novamente.',
        type: 'warning',
        duration: 5000
      });

      // Redireciona para a página de login correta
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const parsed = JSON.parse(user);
          if (parsed.role === 'school') {
            window.location.href = '/school/login';
          } else if (parsed.role === 'nakota') {
            window.location.href = '/nakota/login';
          } else {
            window.location.href = '/clients/login';
          }
        } catch(e) {
          window.location.href = '/';
        }
      } else {
        // Se a rota atual começa com /nakota, manda pro login do nakota
        if (window.location.pathname.startsWith('/nakota')) {
          window.location.href = '/nakota/login';
        } else {
          window.location.href = '/';
        }
      }
      
      throw new Error('Sessão expirada');
    }

    // Exigência de troca de senha inicial: intercepta e atualiza o localstorage para forçar o modal
    if (response.status === 403 && data && data.code === 'PASSWORD_CHANGE_REQUIRED') {
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const user = JSON.parse(stored);
          if (user && user.role === 'client' && !user.must_change_password) {
            user.must_change_password = true;
            localStorage.setItem('user', JSON.stringify(user));
            // Recarrega a página para atualizar o estado global do componente TopToolComp
            window.location.reload();
          }
        } catch (e) {
          console.error(e);
        }
      }
    }

    if (data && data.status === 'error') {
      throw new Error(data.message);
    }

    if (!response.ok) {
      const errorMsg = (data && data.message) || `Erro no servidor: HTTP ${response.status}`;
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    
    let userFriendlyMessage = error.message || 'Não foi possível se conectar ao servidor.';
    if (error.message === 'Failed to fetch') {
      userFriendlyMessage = `Não foi possível acessar o servidor. Verifique a internet ou acesse o link para ver o erro direto: ${url}`;
    }

    // Reporta ao Telegram (exceto sessão expirada e o próprio endpoint de erro)
    if (error.message !== 'Sessão expirada' && !path.includes('error-report')) {
      reportErrorToTelegram({
        error: `[API] ${error.message}`,
        stack: error.stack || '',
        component: `apiRequest(${path})`,
        url: window.location.href
      });
    }

    // Não exibe toast para erros de sessão expirada (já tratado acima)
    if (error.message !== 'Sessão expirada') {
      const msgLower = userFriendlyMessage.toLowerCase();
      const isTechnical = msgLower.includes('sql') || 
                          msgLower.includes('pdo') || 
                          msgLower.includes('database') || 
                          msgLower.includes('tabela') || 
                          msgLower.includes('coluna') || 
                          msgLower.includes('syntax') || 
                          msgLower.includes('connection') || 
                          msgLower.includes('failed to fetch') || 
                          msgLower.includes('fatal') || 
                          msgLower.includes('exception') || 
                          msgLower.includes('http');

      if (!isTechnical) {
        // Alerta centralizado amigável para regras de negócio e validações
        store.dispatch('showToast', {
          title: 'Aviso',
          message: userFriendlyMessage,
          type: 'warning',
          isCentered: true,
          duration: 0
        });
      } else {
        // Erro técnico de conexão, PHP ou banco de dados (mantém como topo)
        store.dispatch('showToast', {
          title: 'Erro de Conexão / PHP',
          message: userFriendlyMessage,
          type: 'error',
          duration: 10000
        });
      }
    }
    
    throw error;
  }
}
